'use client';
import React, { useRef, useState, useEffect } from 'react';

type Tab = 'upload' | 'url';

interface Props {
  open: boolean;
  currentSrc: string;
  accept?: string;
  onConfirm: (src: string) => void;
  onClose: () => void;
}

export function MediaPickerModal({ open, currentSrc, accept, onConfirm, onClose }: Props) {
  const [tab, setTab] = useState<Tab>('upload');
  const [urlValue, setUrlValue] = useState('');
  const [uploading, setUploading] = useState(false);
  const [drag, setDrag] = useState(false);
  const [uploadedPreview, setUploadedPreview] = useState('');
  const [urlError, setUrlError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const urlInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setTab('upload');
      setUrlValue(currentSrc.startsWith('http') ? currentSrc : '');
      setUploadedPreview('');
      setUrlError('');
    }
  }, [open, currentSrc]);

  useEffect(() => {
    if (open && tab === 'url') urlInputRef.current?.focus();
  }, [open, tab]);

  if (!open) return null;

  const upload = async (file: File) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch('/api/upload', { method: 'POST', body: fd });
      if (!res.ok) throw new Error(await res.text());
      const { url } = await res.json();
      setUploadedPreview(url);
      onConfirm(url);
    } catch (e: any) {
      alert(e?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDrag(false);
    const f = e.dataTransfer.files?.[0];
    if (f) upload(f);
  };

  const confirmUrl = async () => {
    const v = urlValue.trim();
    if (!v) { setUrlError('Please enter a URL'); return; }
    if (!/^https?:\/\/.+/i.test(v)) { setUrlError('URL must start with http:// or https://'); return; }
    setUrlError('');

    // Skip re-upload if already on our GCS bucket
    if (v.includes('storage.googleapis.com/cms_rewire')) {
      onConfirm(v);
      return;
    }

    setUploading(true);
    try {
      const res = await fetch('/api/media/download', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: v }),
      });
      if (res.ok) {
        const { url: gcsUrl } = await res.json();
        onConfirm(gcsUrl);
      } else {
        // Host not allowlisted or fetch failed — fall back to raw URL
        onConfirm(v);
      }
    } catch {
      onConfirm(v);
    } finally {
      setUploading(false);
    }
  };

  const isImage = !accept || accept.includes('image');

  return (
    <div className="mpicker-backdrop" onClick={onClose}>
      <div className="mpicker-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Choose media">

        {/* Header */}
        <div className="mpicker-header">
          <span className="mpicker-title">Change media</span>
          <button type="button" className="mpicker-close" onClick={onClose} aria-label="Close">
            <svg width="16" height="16" fill="currentColor" viewBox="0 0 256 256"><path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z"/></svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="mpicker-tabs">
          <button type="button" className={`mpicker-tab${tab === 'upload' ? ' mpicker-tab--active' : ''}`} onClick={() => setTab('upload')}>
            <svg width="14" height="14" fill="currentColor" viewBox="0 0 256 256"><path d="M224,152v56a16,16,0,0,1-16,16H48a16,16,0,0,1-16-16V152a8,8,0,0,1,16,0v56H208V152a8,8,0,0,1,16,0ZM88.49,88.49,120,57V168a8,8,0,0,0,16,0V57l31.51,31.52a8,8,0,0,0,11.32-11.32l-48-48a8,8,0,0,0-11.32,0l-48,48A8,8,0,0,0,88.49,88.49Z"/></svg>
            Upload from device
          </button>
          <button type="button" className={`mpicker-tab${tab === 'url' ? ' mpicker-tab--active' : ''}`} onClick={() => setTab('url')}>
            <svg width="14" height="14" fill="currentColor" viewBox="0 0 256 256"><path d="M137.54,186.36a8,8,0,0,1,0,11.31l-9.94,10A56,56,0,0,1,48.38,128.4L72.5,104.28A56,56,0,0,1,149.31,102a8,8,0,1,1-10.64,11.96,40,40,0,0,0-54.85,1.63L59.7,139.71a40,40,0,1,0,56.58,56.58l9.94-9.94A8,8,0,0,1,137.54,186.36Zm70.08-138a56.08,56.08,0,0,0-79.22,0l-9.94,9.95a8,8,0,0,0,11.32,11.31l9.94-9.94a40,40,0,0,1,56.58,56.58L172.18,140a40,40,0,0,1-54.85,1.62,8,8,0,1,0-10.64,11.97,56,56,0,0,0,76.81-1.63l24.12-24.12A56.08,56.08,0,0,0,207.62,48.38Z"/></svg>
            Enter URL
          </button>
        </div>

        {/* Tab: Upload */}
        {tab === 'upload' && (
          <div className="mpicker-body">
            <div
              className={`mpicker-dropzone${drag ? ' mpicker-dropzone--drag' : ''}`}
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
              onDragLeave={() => setDrag(false)}
              onDrop={onDrop}
            >
              {uploading ? (
                <span className="mpicker-uploading">Uploading…</span>
              ) : uploadedPreview && isImage ? (
                <img src={uploadedPreview} alt="Preview" className="mpicker-preview-img" />
              ) : (
                <>
                  <svg className="mpicker-dropzone-icon" width="32" height="32" fill="currentColor" viewBox="0 0 256 256"><path d="M224,152v56a16,16,0,0,1-16,16H48a16,16,0,0,1-16-16V152a8,8,0,0,1,16,0v56H208V152a8,8,0,0,1,16,0ZM88.49,88.49,120,57V168a8,8,0,0,0,16,0V57l31.51,31.52a8,8,0,0,0,11.32-11.32l-48-48a8,8,0,0,0-11.32,0l-48,48A8,8,0,0,0,88.49,88.49Z"/></svg>
                  <p className="mpicker-dropzone-label">
                    {drag ? 'Drop to upload' : 'Click to browse or drag & drop'}
                  </p>
                  <p className="mpicker-dropzone-hint">
                    {accept ? accept.replace(/\*/g, 'files') : 'Any file type'}
                  </p>
                  {currentSrc && !currentSrc.startsWith('http') && (
                    <p className="mpicker-current">Current: <code>{currentSrc}</code></p>
                  )}
                </>
              )}
            </div>
            <input
              ref={inputRef}
              type="file"
              accept={accept}
              style={{ display: 'none' }}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ''; }}
            />
          </div>
        )}

        {/* Tab: URL */}
        {tab === 'url' && (
          <div className="mpicker-body">
            <label className="mpicker-url-label">
              Media URL
              <input
                ref={urlInputRef}
                type="url"
                className={`mpicker-url-input${urlError ? ' mpicker-url-input--error' : ''}`}
                placeholder="https://example.com/image.jpg"
                value={urlValue}
                onChange={(e) => { setUrlValue(e.target.value); setUrlError(''); }}
                onKeyDown={(e) => { if (e.key === 'Enter') confirmUrl(); }}
              />
              {urlError && <span className="mpicker-url-error">{urlError}</span>}
            </label>
            {urlValue && /^https?:\/\/.+/i.test(urlValue) && isImage && (
              <div className="mpicker-url-preview">
                <img src={urlValue} alt="Preview" className="mpicker-preview-img" onError={(e) => (e.currentTarget.style.display = 'none')} />
              </div>
            )}
            <div className="mpicker-url-actions">
              {currentSrc && (
                <button type="button" className="mpicker-btn mpicker-btn--ghost" onClick={() => { setUrlValue(currentSrc.startsWith('http') ? currentSrc : ''); }}>
                  Reset to current
                </button>
              )}
              <button type="button" className="mpicker-btn mpicker-btn--primary" onClick={confirmUrl}>
                Apply URL
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
